import React, { useMemo } from 'react';

import { Icon } from '../../../common/Icon';
import { TRawAssessmentResult } from '../services/validateDescription';

const COLORS = {
  text: {
    pass: 'text-green-600',
    fail: 'text-red-500',
    neutral: 'text-black',
    secondary: 'text-[#8C8C8C]',
  },
};

const getTextColor = (value: boolean | null, neutral = false): string => {
  if (neutral) return COLORS.text.neutral;
  return value ? COLORS.text.neutral : COLORS.text.fail;
};

function AssessmentItem({
  text,
  value,
  neutral = false,
}: {
  text: string;
  value: boolean | null;
  neutral?: boolean;
}): JSX.Element {
  const textColor = getTextColor(value, neutral);
  return (
    <div
      className={`flex items-center transition-all duration-300 ${textColor} ${
        textColor === COLORS.text.fail ? 'animate-slide-right' : ''
      }`}
    >
      <span className="mr-2">
        {neutral ? (
          <span className="mr-2">•</span>
        ) : (
          <Icon
            className={`w-3.5 h-3.5 ${
              textColor === COLORS.text.fail ? 'animate-scale-up' : ''
            }`}
            name={value ? 'check' : 'cross'}
          />
        )}
      </span>
      <span>{text}</span>
    </div>
  );
}

const assessmentItems = [
  {
    text: 'Start with a past-tense verb (e.g., Researched, Synced, Completed, Decided)',
    key: 'starts_with_past_tense_of_action_verb',
    neutral: true,
  },
  {
    text: 'Begin every sentence with a capital letter',
    key: 'starts_with_capital_letter',
    neutral: true,
  },
  {
    text: 'Update should be grammatically correct',
    key: 'grammatically_correct',
    neutral: true,
  },
  {
    text: 'Include follow-up sentences without breaking them into new lines',
    key: null,
    neutral: true,
  },
  {
    text: 'Not contain special characters or bullet points',
    key: 'does_not_contain_bullet_formatting',
    neutral: true,
  },
  {
    text: 'Include enough details so that the reader will not have followup questions',
    key: 'should_contain_details_of_action_performed',
    neutral: true,
  },
];

export function CheckInExample({
  assessment,
}: {
  assessment: TRawAssessmentResult | null;
}): JSX.Element {
  const config = useMemo(() => {
    return assessment
      ? assessmentItems.map((item) => {
          const hasKey = item.key && item.key in assessment;
          return hasKey
            ? {
                ...item,
                value: assessment[item.key as keyof TRawAssessmentResult] as
                  | boolean
                  | null,
                neutral: false,
              }
            : item;
        })
      : assessmentItems;
  }, [assessment]);
  return (
    <div className="text-sm font-nunito space-y-2">
      {config.map(({ text, key, neutral }) => (
        <AssessmentItem
          key={text}
          text={text}
          value={
            key
              ? (assessment?.[key as keyof TRawAssessmentResult] as
                  | boolean
                  | null)
              : null
          }
          neutral={neutral || false}
        />
      ))}
    </div>
  );
}
